import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';

import { setProgressValue } from '../Modal/actions';
import { logExport } from './actions';

import {
  formatDate,
  loadServiceData,
  sortByKey,
  toSentenceCase,
} from '../../utils';
import { getFullName, getRoleName } from '../Person/utils';

import * as buildingSelectors from '../Building/selectors';

export const getPmcsWithBuildingId = (buildings) => {
  if (!buildings || !Array.isArray(buildings) || buildings.length < 1)
    return {};
  const pmcs = {};
  buildings.forEach(({ id, parent }) => {
    const previousBuildingsId = get(pmcs, `${parent.id}.buildings`, []);
    pmcs[parent.id] = {
      id: parent.id,
      name: parent.name,
      buildings: [...previousBuildingsId, id],
    };
  });
  return pmcs;
};

export const formatPmcOptions = (pmcs) => {
  if (!pmcs || isEmpty(pmcs)) return [];
  return Object.keys(pmcs)
    .map((pmcId) => ({
      label: pmcs[pmcId].name,
      value: pmcs[pmcId].id,
    }))
    .sort((a, b) => sortByKey(a, b, 'label'));
};

export const formatBuildingOptions = (buildings, isSRExport) => {
  if (!buildings || !Array.isArray(buildings) || buildings.length < 1)
    return [];
  return buildings.map(({ name, id: value, externalServiceRequest }) => {
    const label =
      isSRExport && externalServiceRequest
        ? `${name} (This building has external Service Requests)`
        : name;
    const disabled = isSRExport && externalServiceRequest; // in v3 should be updated to isDisabled
    return {
      label,
      value,
      disabled,
    };
  });
};

/** Filters buildings list based on list of pmcs
 * @param {Array} buildings - List of buildings with `parent.id`
 * @param {Array} selectedPmcs - List of pmcs
 * @returns {Array} List of buildings
 */
export const filterBuildings = (buildings, selectedPmcs) => {
  if (!selectedPmcs || !Array.isArray(buildings) || selectedPmcs.length < 1)
    return buildings;
  return buildings.filter((building) =>
    selectedPmcs.find((pmcId) => pmcId === get(building, 'parent.id')),
  );
};

export const getValueByDeliveryKey = (key, delivery, isCSV) => {
  const {
    pmcName,
    buildingName,
    unitName,
    recipient,
    type,
    createdAt,
    comments,
    pickUp,
    signatureRequired,
  } = delivery;
  switch (key) {
    case 'pmc':
      return pmcName;
    case 'building':
      return buildingName;
    case 'unit':
      return unitName;
    case 'recipient': {
      const { firstName, lastName } = recipient || {};
      if (firstName || lastName) {
        return `${firstName} ${lastName}`;
      }
      return 'Unit delivery';
    }
    case 'type': {
      return type?.displayName;
    }
    case 'delivery_time':
      return isCSV ? formatDate(createdAt) : new Date(createdAt);
    case 'comments':
      return comments;
    case 'pick_up':
      return String(pickUp).substring(0, 1);
    case 'signature_required':
      return String(signatureRequired).substring(0, 1);
    default:
      return 'value';
  }
};

export const getDeliveryTableData = (deliveryData, formData, isCSV) => {
  const headers = [];
  const rows = [];
  Object.keys(formData).forEach((key) => {
    if (formData[key] === true) headers.push(key);
  });
  deliveryData?.forEach((delivery) => {
    const row = [
      ...headers.map((key) => getValueByDeliveryKey(key, delivery, isCSV)),
    ];
    rows.push(row);
  });
  return [headers, ...rows];
};

const getFormatDate = (isCSV, date) => {
  let formatDate = '';
  if (date) {
    formatDate = isCSV ? formatDate(date) : new Date(date);
  }
  return formatDate;
};

export const getValueBySRKey = (key, sr, isCSV) => {
  const {
    pmcName,
    buildingName,
    unitName,
    createdAt,
    categoryName,
    firstUpdateTime,
    updatedAt,
    status,
  } = sr;
  switch (key) {
    case 'pmc':
      return pmcName;
    case 'property':
      return `${buildingName}${unitName ? ` / ${unitName}` : ''}`;
    case 'open_time':
      return getFormatDate(isCSV, createdAt);
    case 'category':
      return categoryName;
    case 'first_update_time':
      return getFormatDate(isCSV, firstUpdateTime);
    case 'last_update_time':
      return getFormatDate(isCSV, updatedAt);
    case 'current_status':
      return status;
    default:
      return 'value';
  }
};

export const getSRTableData = (SRData, formData, isCSV) => {
  const headers = [];
  const rows = [];
  Object.keys(formData).forEach((key) => {
    if (formData[key] === true) headers.push(key);
  });
  SRData?.forEach((sr) => {
    const row = [...headers.map((key) => getValueBySRKey(key, sr, isCSV))];
    rows.push(row);
  });
  return [headers, ...rows];
};

const getValueByResidentKey = (key, resident, isCSV) => {
  const {
    pmcName,
    buildingName,
    firstName,
    lastName,
    residentRoles,
    boardMemberRoles,
    contactInfo,
    createdAt,
    lastActivityTime,
    authInfo,
    isActive,
  } = resident;
  switch (key) {
    case 'pmc':
      return pmcName;
    case 'building':
      return buildingName;
    case 'unit':
      return get(residentRoles, '[0].propertyName', '');
    case 'role': {
      const roleId = get(residentRoles, '[0].role');
      const roleName = getRoleName(roleId, loadServiceData().role);
      const otherRolesNumber =
        residentRoles.length + boardMemberRoles.length - 1;
      if (otherRolesNumber && otherRolesNumber > 0) {
        return `${roleName} (+${otherRolesNumber} ${
          otherRolesNumber > 1 ? 'roles' : 'role'
        })`;
      }
      return roleName;
    }
    case 'name':
      return `${firstName} ${lastName}`;
    case 'phone_number': {
      const phoneNumber = contactInfo?.find(
        (contact) => contact.type === 'phone_number',
      );
      return phoneNumber ? phoneNumber.contactInfo : '';
    }
    case 'email': {
      const email = contactInfo?.find((contact) => contact.type === 'email');
      return email ? email.contactInfo : '';
    }
    case 'created_at':
      return isCSV ? formatDate(createdAt) : new Date(createdAt);
    case 'registered':
      return authInfo.type ? 'yes' : 'no';
    case 'last_activity_time':
      return isCSV ? formatDate(lastActivityTime) : new Date(lastActivityTime);
    case 'status':
      return isActive ? 'Current' : 'Moved Out';
    default:
      return 'value';
  }
};

export const getResidentsTableData = (residentsData, formData, isCSV) => {
  const headers = [];
  const rows = [];
  Object.keys(formData).forEach((key) => {
    if (key !== 'includeMovedOut' && formData[key] === true) headers.push(key);
    return null;
  });
  residentsData?.forEach((resident) => {
    const row = [
      ...headers.map((key) => getValueByResidentKey(key, resident, isCSV)),
    ];
    rows.push(row);
  });
  return [headers, ...rows];
};

export const formatVisitorData = (data) => {
  if (!data?.length) return null;
  const uniqueVisitors = {};
  data.forEach((visitor) => {
    const personId = visitor?.person?.id;
    if (!personId) return;
    if (uniqueVisitors?.[personId]) {
      uniqueVisitors[personId].push(visitor);
    } else {
      uniqueVisitors[personId] = [visitor];
    }
  });
  const output = [];
  Object.keys(uniqueVisitors).forEach((key) => {
    const personVisitorRoles = uniqueVisitors[key];
    personVisitorRoles.sort(
      (a, b) => Date.parse(b?.createdAt) - Date.parse(a?.createdAt),
    );
    const [mostRecentVisitorRole] = personVisitorRoles;
    const userDownloadedApp = personVisitorRoles.some(
      ({ appInstalled }) => appInstalled,
    );
    const userHasInvite = personVisitorRoles.some(
      ({ inviteSent }) => inviteSent,
    );
    const userActive = personVisitorRoles.some(({ validUntil }) => {
      if (validUntil) {
        return Date.now() < Date.parse(validUntil);
      }
      return true;
    });
    return output.push({
      ...mostRecentVisitorRole,
      userDownloadedApp,
      userHasInvite,
      userActive,
    });
  });
  return output;
};

export const getValueByVisitorKey = (key, visitor, isCSV) => {
  const {
    pmcName,
    buildingName,
    unitName,
    person,
    createdAt,
    authorizer,
    validUntil,
    userActive,
    userDownloadedApp,
    userHasInvite,
    comments,
  } = visitor;
  switch (key) {
    case 'pmc':
      return pmcName;
    case 'property':
      return `${buildingName}${unitName ? ` / ${unitName}` : ''}`;
    case 'first_name':
      return person?.firstName ?? '';
    case 'last_name':
      return person?.lastName ?? '';
    case 'date_person_created':
      return isCSV
        ? formatDate(person?.createdAt)
        : new Date(person?.createdAt);
    case 'date_visitor_added':
      return isCSV ? formatDate(createdAt) : new Date(createdAt);
    case 'host_role':
      return authorizer?.role ? getRoleName(authorizer.role) : 'admin';
    case 'valid_until': {
      if (validUntil) {
        if (Date.now() > Date.parse(validUntil)) {
          return 'Expired';
        }
        return isCSV ? formatDate(validUntil) : new Date(validUntil);
      }
      return 'Permanent';
    }
    case 'expired':
      return userActive ? 'No' : 'Yes';
    case 'invited':
      return userHasInvite ? 'Yes' : 'No';
    case 'downloaded_app':
      return userDownloadedApp ? 'Yes' : 'No';
    case 'comments':
      return comments;
    default:
      return 'value';
  }
};

export const getValueByVisitorLogKey = (key, visitor) => {
  const { pmcName, buildingName, person, role, doors, source, comments } =
    visitor;
  switch (key) {
    case 'pmc':
      return pmcName;
    case 'building':
      return buildingName;
    case 'visitor':
      return getFullName(person);
    case 'role':
      return toSentenceCase(getRoleName(role));
    case 'door':
      return doors?.map(({ name }) => name).join(', ');
    case 'open_method':
      return buildingSelectors.getDoorOpeningMethodName(source);
    case 'comments':
      return comments;
    default:
      return 'value';
  }
};

export const getVisitorTableData = (
  visitorData,
  formData,
  isVisitorLog,
  isCSV,
) => {
  const headers = [];
  const rows = [];
  Object.keys(formData).forEach((key) => {
    if (formData[key] === true) headers.push(key);
  });
  visitorData?.forEach((visitor) => {
    const row = [
      ...headers.map((key) =>
        isVisitorLog
          ? getValueByVisitorLogKey(key, visitor)
          : getValueByVisitorKey(key, visitor, isCSV),
      ),
    ];
    rows.push(row);
  });
  return [headers, ...rows];
};

export const setIsActiveFlag = (values, isActive) =>
  values.map((value) => ({ ...value, isActive }));

export const formatResults = (active, inactive) => {
  const activeResults = setIsActiveFlag(get(active, 'data', []), true);
  const inactiveResults = setIsActiveFlag(get(inactive, 'data', []), false);
  return {
    ...active,
    ...inactive,
    data: [...activeResults, ...inactiveResults],
  };
};

export async function* fetchData(
  dispatch,
  buildingIds,
  getItems,
  startDate,
  endDate,
  actionType,
  id,
) {
  if (buildingIds?.length) {
    const ids = [...buildingIds];

    while (ids.length > 0) {
      const buildingId = ids.pop();
      const params = {
        disable_pagination: true,
        created_at_after: startDate,
        created_at_before: endDate,
        building: buildingId,
        use_replica: true,
      };
      const result = await getItems(dispatch, params, true);
      result.buildingId = buildingId;
      const progressValue = Math.round(
        (100 / buildingIds.length) * (buildingIds.length - ids.length),
      );
      logExport(dispatch, params?.building, actionType);

      setProgressValue(dispatch, progressValue, id);
      yield result;
    }
  }
}

export async function* getServiceRequestIterator(
  dispatch,
  requestLimit,
  getServiceRequests,
  offsets,
  id,
  building,
  controller,
) {
  if (offsets?.length) {
    const ids = [...offsets];

    while (ids.length > 0) {
      const offset = ids.pop();
      const queryParams = {
        limit: requestLimit,
        offset,
      };
      if (building) {
        queryParams.building = building;
      }
      const result = await getServiceRequests(
        dispatch,
        { ...queryParams, controller },
        true,
      );

      if (!result && !result?.data) {
        throw new Error();
      }

      const progressValue = Math.round(
        (100 / offsets.length) * (offsets.length - ids.length),
      );
      setProgressValue(dispatch, progressValue, id);
      yield result;
    }
  }
}
export async function* getSRFetchData(
  dispatch,
  buildingIds,
  getServiceRequests,
  id,
  controller,
) {
  if (buildingIds?.length) {
    const ids = [...buildingIds];

    while (ids.length > 0) {
      const buildingId = ids.pop();
      const queryParams = {
        disable_pagination: true,
        building: buildingId,
        controller,
      };
      const result = await getServiceRequests(dispatch, queryParams, true);

      if (!result && !result?.data) {
        throw new Error();
      }
      const progressValue = Math.round(
        (100 / buildingIds.length) * (buildingIds.length - ids.length),
      );
      setProgressValue(dispatch, progressValue, id);
      yield result;
    }
  }
}

export const formatFormValues = (formValues) => {
  const { startDate: start, endDate: end, ...rest } = formValues;
  let startDate = null;
  let endDate = null;

  if (start && moment(start).isValid()) {
    startDate = moment(start).set({ h: 0, m: 0, s: 0 }).format();
  }
  if (end && moment(end).isValid()) {
    endDate = moment(end).set({ h: 23, m: 59, s: 59 }).format();
  }
  return { ...rest, startDate, endDate };
};
